import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IEvent, IrbAppEvents, IrbClientsEvents} from "@irb-common/events";
import {InventoryService} from '@irb/irb-orders/order-blotter/inventory/inventory.service';
import {InventoryComponent} from '@irb/irb-orders/order-blotter/inventory/inventory.component';
import {IBidAndAskPrices} from "@irb/irb-orders/irb-orders.model";
import {InlineOrderStatusAction} from "@irb/irb-orders/order-blotter/inventory/inline-order-status-action";
import {IAlertDialogData} from "@irb/app.model";
import {AnalyticsService} from "@irb-common/analytics/analytics.service";

@Component({ standalone: false,
  selector: 'irb-edit-trading-instructions',
  templateUrl: './edit-trading-instructions.component.html',
  styleUrls: ['./edit-trading-instructions.component.scss', '../../irb-common/styles/dialog.scss', '../editor-sidebar/editor-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class EditTradingInstructionsComponent  extends IrbEditor {

  @ViewChild("body") body: ElementRef;

  currentOrderData: any;
  expirationsTabInit: boolean;
  tradeInstructionsTabInit: boolean;
  notHeldTabInit: boolean;
  _collapseForOpenTriggers: boolean;
  orderHistoryTabInit:boolean;
  reinvestmentinit:boolean;
  expandablePanels: Array<string> = ['TRADE_INSTRUCTIONS', 'TRADE_DETAILS', 'REINVESTMENT', 'ORDER_HISTORY'];
  defaultExpandedPanels: Array<string> = ['TRADE_DETAILS', 'TRADE_INSTRUCTIONS', 'REINVESTMENT'];
  inventoryComponentRef: InventoryComponent;
  editTradingInstructionsRef: EditTradingInstructionsComponent;
  selectedOrdersArr: any[] = [];
  actionType: string = '';
  bidAndAskPrices: IBidAndAskPrices;
  swapBidAndAskPrices: IBidAndAskPrices;
  bidAndAskPricesReceived = false;
  pfs: InventoryService;
  disableMode = false;
  multiSymbol = false;
  showUnsavedChangesWarning = false;
  isAllMutualFund = false;
  isUiTest = false;
  slideOutTitle: string;

  constructor(private cdr: ChangeDetectorRef, public inlineOSAction: InlineOrderStatusAction, public ats: AnalyticsService) {
    super();
    this.identifier = "orderId";
  }
  get(event: IEvent) {
    const self = this;
    super.get(event);
    // TODO: As discusse with Hari, this is required since the selected orders need to be reset on every user click.
    self.selectedOrdersArr = [];
    self.bidAndAskPrices = {} as IBidAndAskPrices;
    self.bidAndAskPricesReceived = false;


    self.inventoryComponentRef = event.data.parent;
    self.editTradingInstructionsRef = self;

    self.initExpansionPanels(event);

    self.actionType = event.data.actionType;
    self.multiSymbol = false;
    if (self.inventoryComponentRef) {
      if (self.inventoryComponentRef.current === 'activeOrders') {
        self.ats.trackOrderBlotterActiveTradeDetailsEvent();
      } else if (self.inventoryComponentRef.current === 'resolvedOrders') {
        self.ats.trackOrderBlotterClosedTradeDetailsEvent();
      }

      self.pfs = self.inventoryComponentRef.pfs;
      self.pfs.init();
      self.isModified = self.pfs.updateOrderPayload.isModified;
      self.isValid = self.pfs.updateOrderPayload.isValid();
      self.disableMode =false;
      if (self.actionType === 'MultiEdit') {
        self.currentOrderData = event.data.data;
        self.selectedOrdersArr = self.currentOrderData;
        if (self.currentOrderData.length === 1) {
          self.actionType = 'SingleEdit';
          self.currentOrderData = self.currentOrderData[0];
          if (self.currentOrderData.securityType === "Mutual Fund") {
            self.isAllMutualFund = true;
          }
        } else if (self.currentOrderData.length > 1) {
          const symbol = self.currentOrderData[0].securitySymbol;
          self.isAllMutualFund = true;
          self.currentOrderData.forEach((orderData: any) => {
            if (orderData.securitySymbol !== symbol) {
              self.multiSymbol = true;
            }
            if (orderData.securityType !== "Mutual Fund") {
              self.isAllMutualFund = false;
            }
          });

        }
      } else {
        self.currentOrderData = event.data.data.data;
        if (self.currentOrderData.securityType === "Mutual Fund") {
          self.isAllMutualFund = true;
        }
        self.selectedOrdersArr.push(self.currentOrderData);
      }

      if (self.actionType !== 'MultiEdit') {
        if (self.currentOrderData.securitySymbol.indexOf('#') === -1) {
          self.getBidAndAskPrices(this.currentOrderData.securitySymbol, this.currentOrderData.securityType);

          if(this.currentOrderData.securityType?.toUpperCase() === "OPTION" && this.currentOrderData.optionStrategyType?.toUpperCase() === "SPREAD") {
            self.getSwapBidAndAskPrices(this.currentOrderData.swapExchangeSymbol, this.currentOrderData.securityType);
          }
        } else {
          self.bidAndAskPricesReceived = true;
        }
      } else if (!this.multiSymbol && self.currentOrderData.length > 1) {
          self.getBidAndAskPrices(this.currentOrderData[0].securitySymbol, this.currentOrderData[0].securityType);
        } else {
        self.bidAndAskPricesReceived = true;
      }

    }
    this.disableMode = ((this.currentOrderData.orderStatus === "APPROVED" || this.currentOrderData.orderStatus === "WARNING" || this.actionType === "Replace" ) && this.inventoryComponentRef.current === "activeOrders" ) ? false : true;
    this.slideOutTitle = (this.actionType === 'Replace') ? 'Edit Trade Order':'Trade Details';
    this.load({});
  }

  load(data: any) {
    super.load(data);
    setTimeout(() => this.onResize());
  }

  getBidAndAskPrices(symbol: string, securityType: string) {
    if (!this.disableMode) {
      this.inventoryComponentRef.pfs.getBidAndAskPrices(symbol, securityType)
        .subscribe((resp: any) => {
          if (resp && resp.responseStatus === 'SUCCESS') {
            this.bidAndAskPrices = resp.responseData;
            this.bidAndAskPrices.lastPrice = (resp.responseData.lastPrice) ? resp.responseData.lastPrice : resp.responseData.lastTrade;
            this.bidAndAskPricesReceived = true;
          }
        });
    } else {
      this.bidAndAskPricesReceived = true;
      const item = this.selectedOrdersArr[0] || {};
      const staticVal:IBidAndAskPrices = {
        ask: (item.askPrice)  ? item.askPrice : 0.00,
        bid: (item.bidPrice) ? item.bidPrice : 0.00,
        midpoint: (item.midpointPrice)  ? item.midpointPrice : 0.00,
        lastPrice: (item.currentUnitPrice == null) ? null : item.currentUnitPrice.toFixed(2)
       };
       this.bidAndAskPrices = staticVal;
    }
  }
      
  getSwapBidAndAskPrices(symbol: string, securityType: string) {
    if (!this.disableMode) {
      this.inventoryComponentRef.pfs.getBidAndAskPrices(symbol, securityType)
        .subscribe((resp: any) => {
          if (resp && resp.responseStatus === 'SUCCESS') {
            this.swapBidAndAskPrices = resp.responseData;
            this.swapBidAndAskPrices.lastPrice = (resp.responseData.lastPrice) ? resp.responseData.lastPrice : resp.responseData.lastTrade;
          }
        });
    } else {
      const item = this.selectedOrdersArr[0] || {};
      const staticVal:IBidAndAskPrices = {
        ask: (item.askPrice)  ? item.askPrice : 0.00,
        bid: (item.bidPrice) ? item.bidPrice : 0.00,
        midpoint: (item.midpointPrice)  ? item.midpointPrice : 0.00,
        lastPrice: (item.currentUnitPrice == null) ? null : item.currentUnitPrice.toFixed(2)
       };
       this.swapBidAndAskPrices = staticVal;      
    }
  }

  initExpansionPanels(event: IEvent) {
    this.expirationsTabInit = event.data.expirationsinit || false;
    this.notHeldTabInit = event.data.notheldinit || false;
    this.tradeInstructionsTabInit = event.data.tradeInstructionsinit || false;
    this.orderHistoryTabInit = event.data.orderhistoryinit || false;
    this._collapseForOpenTriggers = (this.expirationsTabInit || this.expirationsTabInit ||this.tradeInstructionsTabInit) ? true : false;
    this.reinvestmentinit = event.data.reinvestmentinit || false;

    let sessionOverridesDefault = true;
    let dep = this.defaultExpandedPanels;
    let initPanelName = 'TRADE_INSTRUCTIONS';

    if (this.orderHistoryTabInit) {
      sessionOverridesDefault = false;
      initPanelName = 'ORDER_HISTORY';
      dep = ['ORDER_HISTORY'];
    }else if (this.tradeInstructionsTabInit) {
      sessionOverridesDefault = false;
      initPanelName = 'TRADE_INSTRUCTIONS';
      dep = ['TRADE_INSTRUCTIONS', 'TRADE_DETAILS', 'REINVESTMENT'];
    }else if(this.reinvestmentinit) {
      sessionOverridesDefault = false;
      initPanelName = 'REINVESTMENT';
      dep = ['REINVESTMENT','TRADE_INSTRUCTIONS'];
    }
    
    this.ies.loadExpansionState(this.expandablePanels.length, initPanelName, dep, sessionOverridesDefault);
  }

  get collapseForOpenTriggers() {
    return this._collapseForOpenTriggers;
  }



  save() {
    const self = this;
    if (self.inventoryComponentRef.pfs && self.inventoryComponentRef.pfs.updateOrderPayload ) {
      const payload = self.inventoryComponentRef.pfs.updateOrderPayload.getPayload();
      this.isValid = self.inventoryComponentRef.pfs.updateOrderPayload.isValid();
      if (!this.isValid) {
          // console.log('test payload', payload);
        let errorMessage = 'The changes cannot be submitted. Please correct errors for fields marked in red.';
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
          dType: 'ERROR',
          dTitle: 'Errors Found',
          dProceedBtnLabel: 'Close',
          msg: errorMessage,
          onProceed: () => {
          }
        } as IAlertDialogData);
        return;
      }
      if (!payload) { return; }
      const params = self.inventoryComponentRef.params;
      const parent = self.inventoryComponentRef;
      if (self.actionType === 'MultiEdit') {
          //console.log('multiedit payload-->', payload);
        self.inventoryComponentRef.updateMultipleOrders(payload);
        this.cancelWithoutSave();
      } else {
        if (self.inventoryComponentRef.pfs.updateOrderPayload.warningMsg) {
          //console.log('warnings-->', payload);
          this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dType: 'WARN',
            dTitle: 'Save Order',
            dProceedBtnLabel: 'Save',
            msg: self.inventoryComponentRef.pfs.updateOrderPayload.warningMsg,
            onProceed: () => {
                // console.log('singleedit payload', payload);
              self.inlineOSAction.updateOrderStatusInLineAction(payload, 'SingleEdit', params, parent);
              this.cancelWithoutSave();
            }
          } as IAlertDialogData);
        } else {
          //console.log('singleedit payload-->', payload);
          self.inlineOSAction.updateOrderStatusInLineAction(payload, 'SingleEdit', params, parent);
          this.cancelWithoutSave();
        }
      }
    }
  }

  // DIDN"T FIND USE OF it hard-coded data
  // openSlideout() {
  //   const data = {
  //     "data": {"portfolioId": 263739},
  //     "slot": "B",
  //     "fullscreen": false
  //   };
  //
  //   this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_PORTFOLIO, data);
  // }

  @HostListener("window:resize", ["$event"])
  onResize() {
    if(this.body) {
      const rect = this.body.nativeElement.getBoundingClientRect();
      let h = (window.innerHeight - rect.top - 10);

      h = (h < 200) ? 200 : h;
      this.bodyHeight = h + "px";
    }
  }
  processEvent(event: IEvent) {
    super.processEvent(event);

    switch (event.type) {
      //Any events not handled by super class are added here
    }
  }
  cancel() {
    this.showUnsavedChangesWarning = this.pfs.updateOrderPayload.isModified;
    if (!this.showUnsavedChangesWarning) {
      super.cancel();
    }
  }
  cancelWithoutSave() {
    this.pfs.updateOrderPayload.isModified = false;
    this.cancel();
  }

}
